/**
 * P53.A — Global ODM ErrorReport notifier hook
 * --------------------------------------------
 * Polls /api/odm-sftp/error-reports for the currently-active ODM env
 * every 3 minutes (only when the tab is visible) and toasts AZAI staff
 * whenever a new HTML ErrorReport lands. Each toast deep-links to the
 * Error Reports tab on /odm/submissions.
 *
 * The "last seen" timestamp is stored in localStorage so reloads don't
 * re-toast already-seen reports.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import api from '../services/api';

const POLL_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
const STARTUP_DELAY_MS = 5 * 1000; // 5s after mount, avoids login race
const STORAGE_KEY = 'azai.odmErrorReportPoller.lastSeenIso';

const _parseDate = (iso) => {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
};

export default function useErrorReportPoller(enabled) {
  const navigate = useNavigate();
  const seedTimerRef = useRef(null);
  const intervalRef = useRef(null);
  // Initialize from localStorage so reloads don't replay old reports.
  const lastSeenRef = useRef(_parseDate(localStorage.getItem(STORAGE_KEY)));
  // Avoid toasting on the very first poll after page load — that's the
  // "catch-up" run; we just rebase the watermark.
  const hasRunOnceRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const persistLastSeen = (ms) => {
      lastSeenRef.current = ms;
      try {
        localStorage.setItem(STORAGE_KEY, new Date(ms).toISOString());
      } catch (storageErr) {
        // localStorage may be unavailable (private mode, disabled cookies).
        // Log at debug so operators can diagnose "notifications keep replaying"
        // reports without noising production observability.
        logger.debug('[useErrorReportPoller] localStorage.setItem failed', {
          op: 'poller_persist_last_seen',
          error_category: 'storage_unavailable',
          message: storageErr?.message,
        });
      }
    };

    const fetchAndDiff = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        // No `env` param ⇒ backend resolves the currently-active env.
        const res = await api.get('/odm-sftp/error-reports');
        const data = res.data || {};
        if (!data.success) return;
        const reports = Array.isArray(data.reports) ? data.reports : [];
        if (reports.length === 0) return;

        const newestMs = reports.reduce(
          (max, r) => Math.max(max, _parseDate(r.modified)),
          0,
        );
        if (!newestMs) return;

        if (!hasRunOnceRef.current) {
          // First successful poll after mount — just establish baseline so we
          // don't toast on every login.
          hasRunOnceRef.current = true;
          if (newestMs > lastSeenRef.current) persistLastSeen(newestMs);
          return;
        }

        const fresh = reports.filter((r) => _parseDate(r.modified) > lastSeenRef.current);
        if (fresh.length === 0) return;

        // Rebase the watermark BEFORE toasting so a re-render doesn't dupe.
        persistLastSeen(newestMs);

        const envLabel = (data.environment || '').toUpperCase() || 'ODM';
        if (fresh.length === 1) {
          const r = fresh[0];
          toast.warning(`New ODM ErrorReport (${envLabel})`, {
            description: r.filename,
            duration: 12000,
            action: {
              label: 'View',
              onClick: () => navigate('/odm/submissions?tab=error-reports'),
            },
          });
        } else {
          toast.warning(`${fresh.length} new ODM ErrorReports (${envLabel})`, {
            description: 'ODM rejected one or more 270/837P files. Open the Error Reports tab to review.',
            duration: 12000,
            action: {
              label: 'View all',
              onClick: () => navigate('/odm/submissions?tab=error-reports'),
            },
          });
        }
      } catch (fetchErr) {
        // Poll is a background nice-to-have; a network blip must never
        // surface a toast to AZAI staff. Log at debug + let the next
        // interval retry pick up any missed reports.
        logger.debug('[useErrorReportPoller] fetch failed', {
          op: 'poller_fetch',
          error_category: fetchErr?.response?.status ? 'http' : 'network',
          status: fetchErr?.response?.status,
          message: fetchErr?.message,
        });
      }
    };

    // Kick off after a brief delay so we don't fire during the initial
    // route hydration / auth bootstrap.
    seedTimerRef.current = setTimeout(() => {
      fetchAndDiff();
      intervalRef.current = setInterval(fetchAndDiff, POLL_INTERVAL_MS);
    }, STARTUP_DELAY_MS);

    // Also re-fetch when the tab regains focus after being hidden a while.
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchAndDiff();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      if (seedTimerRef.current) clearTimeout(seedTimerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled, navigate]);
}
